import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, Chip, ListItem, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import pluralize from "pluralize";
import { useMemo } from "react";

/**
 * How one file is doing, in the vocabulary the analyzing dialogs display.
 * Each dialog maps its own source onto this: document enrichment from
 * `ExtractionStatus`, SDS parsing from its per-file job's `JobStatus`.
 */
export type AnalysisRowStatus = "inProgress" | "done" | "failed";

type ProgressCounts = {
  progressCount: number;
  totalCount: number;
  isFailed: boolean;
  isSucceeded: boolean;
};

export function AnalyzingProgressChip({
  progressCount,
  totalCount,
  isFailed,
  isSucceeded,
}: ProgressCounts) {
  const completedSuccessfully = isSucceeded && progressCount === totalCount;
  const completedUnsuccessfully = isSucceeded && progressCount < totalCount;
  if (isFailed || completedUnsuccessfully) {
    return (
      <Chip
        icon={<CloseIcon fontSize="small" color="error" />}
        label={`${progressCount} of ${totalCount}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "error.main",
          backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
        }}
      />
    );
  }

  if (completedSuccessfully) {
    return (
      <Chip
        icon={<CheckIcon fontSize="small" color="success" />}
        label={`${progressCount} of ${totalCount}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "success.main",
          backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
        }}
      />
    );
  }
  return (
    <Chip
      icon={<CheckIcon fontSize="small" color="info" />}
      label={`${progressCount} of ${totalCount}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        color: "primary.main",
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
      }}
    />
  );
}

export function AnalyzingTitleBar({
  noun,
  count,
  progressCount,
  totalCount,
  isRunning,
  isSucceeded,
  isFailed,
}: ProgressCounts & {
  /** Singular name for what is being analyzed, e.g. "Document". Pluralized as needed. */
  noun: string;
  count: number;
  isRunning: boolean;
}) {
  const title = useMemo(() => {
    const pluralizedNoun = pluralize(noun, count);
    if (isRunning) {
      return `📑 Analyzing ${count} ${pluralizedNoun}...`;
    }
    if (isSucceeded) {
      return `🔔 Finished Analyzing ${pluralizedNoun}`;
    }
    if (isFailed) {
      return `❌ Analysis failed.`;
    }
    return `📑 Analysis complete.`;
  }, [noun, count, isRunning, isSucceeded, isFailed]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: (theme) => theme.spacing(2),
      }}
    >
      <Typography variant="inherit" component="span">
        {title}
      </Typography>
      <AnalyzingProgressChip
        progressCount={progressCount}
        totalCount={totalCount}
        isFailed={isFailed}
        isSucceeded={isSucceeded}
      />
    </Box>
  );
}

export function AnalysisRowStatusDisplay({
  status,
  error,
}: {
  status: AnalysisRowStatus;
  error?: string | null;
}) {
  if (status === "done") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <Typography variant="body2" color="success.main">
          Done
        </Typography>
        <CheckIcon fontSize="small" color="success" />
      </Box>
    );
  }

  if (status === "failed") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <Tooltip title={error ?? "Extraction failed"}>
          <ErrorOutlineIcon color="error" fontSize="small" />
        </Tooltip>
        <Typography variant="body2" color="error.main">
          Failed
        </Typography>
      </Box>
    );
  }

  return (
    <Typography variant="body2" color="primary.main">
      In progress...
    </Typography>
  );
}

export function AnalyzingRow({
  label,
  status,
  error,
}: {
  label: string;
  status: AnalysisRowStatus;
  error?: string | null;
}) {
  return (
    <ListItem
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 0.5,
        }}
      >
        <AnalysisRowStatusDisplay status={status} error={error} />
      </Box>
    </ListItem>
  );
}
